import { TaxEstimateType } from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import {
  WSActions,
  WSCard,
  WSControlGroup,
  WSForm,
  WSGrid,
  WSInfoBox,
  WSInputNumber,
  WSList,
  WSLoader,
  WSText
} from "@wingspanhq/fe-component-library";
import { useWSMutation } from "@ws-react-query";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_USERS_MEMBER_PROFILE } from "../../../query/users/keys";
import { useQueryMemberProfile } from "../../../query/users/queries";
import bookkeepingService from "../../../services/bookkeeping";
import { usersService } from "../../../services/users";
import { DataItem } from "../../../shared/components/DataItem";
import { WSServiceError } from "../../../utils/serviceHelper";
import { useModalHowItWorks } from "../modals/ModalHowItWorks";
import { useModalHowWeCalculateTaxRate } from "../modals/ModalHowWeCalculateTaxRate";
import {
  formatFilingCode,
  formatIncomeEstimate,
  formatStateOfResidence
} from "../utils";

type FormData = {
  preference: "estimated" | "custom";
  customRate?: number | null;
};

type Props = {
  onBack?: () => void;
  onEditTaxProfile?: () => void;
  onNext?: () => void;
  onSkip?: () => void;
  isEdit?: boolean;
};

export const FormTaxRate: React.FC<Props> = props => {
  const userId = useUserId();
  const queryMember = useQueryMemberProfile(userId);

  const currentTaxRate = queryMember.data?.profile.withholdings?.tax?.rate;
  const [estimatedTaxRate, setEstimatedTaxRate] = useState<
    number | undefined
  >();

  const modalHowWeCalculateTaxRate = useModalHowWeCalculateTaxRate();
  const modalHowItWorks = useModalHowItWorks();

  const [submit, submitMeta] = useWSMutation<void, WSServiceError, FormData>(
    async data => {
      const newRate =
        data.preference === "custom" ? data.customRate : estimatedTaxRate;

      await usersService.member.update(userId, {
        memberId: userId,
        profile: {
          withholdings: {
            tax: {
              rate: newRate || 0
            }
          }
        }
      });
    },
    {
      awaitDependencies: [QUERY_USERS_MEMBER_PROFILE],
      onSuccess: props.onNext
    }
  );

  const [runTaxEstimate, runTaxEstimateMeta] = useWSMutation<
    number,
    WSServiceError
  >(
    async () => {
      const taxEstimate = await bookkeepingService.createTaxEstimate({
        type: TaxEstimateType.Base,
        year: new Date().getFullYear()
      });

      const estimatedTaxRate = taxEstimate.taxRates.effective;

      return estimatedTaxRate;
    },
    {
      dependencies: [QUERY_USERS_MEMBER_PROFILE],
      onSuccess: estimatedTaxRate => {
        setEstimatedTaxRate(estimatedTaxRate);
      }
    }
  );

  useEffect(() => {
    runTaxEstimate();
  }, [runTaxEstimate]);

  if (runTaxEstimateMeta.isIdle || runTaxEstimateMeta.isLoading) {
    return <WSLoader.Spinner size="S" />;
  }

  return (
    <WSForm<FormData>
      defaultValues={{
        preference:
          !!currentTaxRate && currentTaxRate !== estimatedTaxRate
            ? "custom"
            : "estimated",
        customRate: currentTaxRate || estimatedTaxRate
      }}
      validationSchema={Yup.object().shape({
        preference: Yup.string().required("Required"),
        customRate: Yup.number().when("preference", {
          is: "custom",
          then: Yup.number()
            .nullable()
            .required("Required")
            .min(1, "Min value is 1")
            .max(100, "Max value is 100")
        })
      })}
      onSubmit={submit}
    >
      {formContext => (
        <WSList gap="2XL">
          <WSCard
            header={{
              label: "Your tax profile",
              value: {
                action: {
                  label: "Edit",
                  onClick: props.onEditTaxProfile,
                  type: "button"
                }
              }
            }}
          >
            <WSList>
              <WSGrid>
                <WSGrid.Item span={{ m: "4" }}>
                  <DataItem
                    label="Filing status"
                    value={formatFilingCode(
                      queryMember.data?.profile.taxInfo?.filingCode
                    )}
                  />
                </WSGrid.Item>
                <WSGrid.Item span={{ m: "4" }}>
                  <DataItem
                    label="Estimated annual income"
                    value={formatIncomeEstimate(
                      queryMember.data?.profile.taxInfo?.incomeEstimateForYear
                    )}
                  />
                </WSGrid.Item>
                <WSGrid.Item span={{ m: "4" }}>
                  <DataItem
                    label="State of residence"
                    value={formatStateOfResidence(
                      queryMember.data?.profile.taxInfo?.stateOfResidence
                    )}
                  />
                </WSGrid.Item>
              </WSGrid>

              <WSInfoBox>
                Your recommended withholding rate: {estimatedTaxRate || 0}% of
                gross payments
              </WSInfoBox>

              <WSText.ParagraphSm color="gray500">
                Your calculations follow IRS Form 1040-ES methodology.{" "}
                <a
                  onClick={() => {
                    modalHowWeCalculateTaxRate.open();
                  }}
                >
                  Learn more
                </a>{" "}
                about your rate calculation.
              </WSText.ParagraphSm>
            </WSList>
          </WSCard>

          <WSCard
            header={{
              label: "Configure your tax settings",
              value: {
                action: {
                  label: "How it works",
                  onClick: () => {
                    const data = formContext.getValues();

                    const taxRate =
                      data.preference === "custom"
                        ? data.customRate
                        : estimatedTaxRate;

                    modalHowItWorks.open({
                      taxRate: taxRate || 0
                    });
                  },
                  type: "button"
                }
              }
            }}
          >
            <WSForm.Field
              name="preference"
              component={WSControlGroup}
              componentProps={{
                type: "radio",
                label: "Set your withholding percentage",
                required: true,
                helperText:
                  "You can adjust this anytime as your income changes.",
                options: [
                  {
                    label: `Use the calculated rate (${
                      estimatedTaxRate || 0
                    }%)`,
                    value: "estimated"
                  },
                  {
                    label: "Custom rate",
                    value: "custom"
                  }
                ]
              }}
            />

            <WSForm.Value name="preference">
              {preference => {
                if (preference !== "custom") {
                  return null;
                }

                return (
                  <WSForm.Field
                    mt="M"
                    name="customRate"
                    component={WSInputNumber}
                    componentProps={{
                      min: 0,
                      max: 100,
                      maxFractionDigits: 2,
                      align: "right",
                      iconRight: "percent"
                    }}
                  />
                );
              }}
            </WSForm.Value>
          </WSCard>

          <WSErrorMessage contextKey="TaxFilling" error={submitMeta.error} />

          <WSList gap="L">
            <WSActions
              alignment="fill"
              buttons={[
                {
                  type: "button",
                  kind: "Tertiary",
                  label: "Back",
                  onClick: props.onBack,
                  visible: !!props.onBack
                },
                {
                  label: "Continue",
                  loading: submitMeta.isLoading
                }
              ]}
            />

            {!props.isEdit && (
              <WSForm.Values names={["preference", "customRate"]}>
                {values => {
                  if (
                    values.preference === "custom" &&
                    values.customRate === 0
                  ) {
                    return (
                      <WSActions
                        alignment="center"
                        buttons={[
                          {
                            type: "button",
                            kind: "Link",
                            label: "Skip, I’ll handle taxes myself",
                            onClick: props.onSkip
                          }
                        ]}
                      />
                    );
                  }

                  return null;
                }}
              </WSForm.Values>
            )}
          </WSList>
        </WSList>
      )}
    </WSForm>
  );
};
